/**
 * HeritageResourcesSection — reusable card grid surfacing the newer
 * heritage-vertical resources on IndianFamilyTree.com and
 * MyFamilyTree.care landings:
 *   1. Historical Families of India & Kerala (feature article)
 *   2. Indian Funeral Traditions & Memorial Practices (feature article)
 *   3. Cemeteries Directory (55 seeded)
 *   4. Funeral Services Directory (32 seeded)
 *
 * Palette is passed in so both saffron-IFT and green-MFT.care palettes
 * render natively without a second design pass.
 */
import { Link } from "react-router-dom";
import {
  ArrowRight, BookOpen, Landmark, Flame, Flower2, TreePine,
} from "lucide-react";

const CARDS = [
  {
    key: "vellaringattu-family",
    to: "/articles/vellaringattu-family-seven-generations-meenachil",
    icon: TreePine,
    kicker: "Family Feature · New",
    title: "The Vellaringattu Family — Seven Generations from the Meenachil River Valley",
    body: "A Kerala Syrian Christian archive of 324 members from Pala, Kottayam. From Thomas Kora and Mariam to a diaspora across Bangalore, Ranchi and the United States — a living portrait of faith, migration, and remembered names.",
    cta: "Read the story",
  },
  {
    key: "historical-families",
    to: "/articles/historical-families-of-india-and-kerala",
    icon: BookOpen,
    kicker: "Feature Article",
    title: "Echoes of Royalty: Historical Families of India & Kerala",
    body: "Mewar, Chola, Travancore, Arakkal, Zamorin — plus Tharavadu lineages, Palakkad Iyers, Marwari business dynasties and freedom-fighter households. 18 min read, translated into Hindi.",
    cta: "Read the article",
  },
  {
    key: "funeral-traditions",
    to: "/articles/indian-funeral-traditions-and-memorial-practices",
    icon: Flame,
    kicker: "Editorial Guide",
    title: "Indian Funeral Traditions & Memorial Practices",
    body: "Hindu, Christian, Muslim, Sikh, Buddhist, Jain, Parsi and tribal traditions — plus regional customs from Kerala to the Northeast. Available in Malayalam, Tamil and Hindi.",
    cta: "Explore traditions",
  },
  {
    key: "cemeteries",
    to: "/cemeteries",
    icon: Landmark,
    kicker: "Directory · 55 listings",
    title: "Cemeteries & Sacred Grounds",
    body: "Christian cemeteries, Muslim kabristans, Hindu ghats, Parsi Towers of Silence, Jewish burial grounds and CWGC military cemeteries — across India and the diaspora.",
    cta: "Browse cemeteries",
  },
  {
    key: "funeral-services",
    to: "/funeral-services",
    icon: Flower2,
    kicker: "Directory · 32 listings",
    title: "Funeral Services Directory",
    body: "Trusted funeral homes, cremation services, embalming and international repatriation providers — searchable by city and service type.",
    cta: "Find a provider",
  },
];

export default function HeritageResourcesSection({ palette, siteScope = "indianfamilytree" }) {
  const P = palette || {
    bg:       "#FDF9EF",
    ink:      "#3A1F0F",
    inkSoft:  "#6B4A2E",
    accent:   "#B25D38",
    accentSoft: "#F7D6C1",
  };
  const accent = P.saffron || P.green || P.accent || "#B25D38";
  const accentSoft = P.saffronSoft || P.greenSoft || P.accentSoft || "#F7D6C1";
  return (
    <section
      className="max-w-6xl mx-auto px-4 py-10"
      data-testid={`heritage-resources-${siteScope}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <BookOpen className="w-4 h-4" style={{ color: accent }} />
        <h2 className="font-display text-2xl" style={{ color: P.ink }}>
          Heritage resources
        </h2>
      </div>
      <p className="text-sm mb-5" style={{ color: P.inkSoft }}>
        Editorial guides and directories built with our community — to help you preserve, honour, and share your family's story.
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        {CARDS.map((c) => {
          const Icon = c.icon;
          return (
            <Link
              key={c.key}
              to={c.to}
              className="group rounded-2xl border p-5 bg-white transition-shadow hover:shadow-md flex flex-col"
              style={{ borderColor: accentSoft }}
              data-testid={`heritage-card-${c.key}`}
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: accentSoft }}
                >
                  <Icon className="w-5 h-5" style={{ color: accent }} />
                </div>
                <div className="min-w-0 flex-1">
                  <div
                    className="text-[10px] font-mono uppercase tracking-wider mb-1"
                    style={{ color: accent }}
                  >
                    {c.kicker}
                  </div>
                  <h3 className="font-display text-base leading-snug" style={{ color: P.ink }}>
                    {c.title}
                  </h3>
                </div>
              </div>
              <p className="text-sm mt-3 leading-relaxed" style={{ color: P.inkSoft }}>
                {c.body}
              </p>
              <div
                className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold group-hover:gap-2 transition-all"
                style={{ color: accent }}
              >
                {c.cta} <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
